import React, { useState, useEffect } from 'react';
import SEOMetaTags from '../components/SEOMetaTags';
import Header from '../components/Header';
import { trackPageView, trackButtonClick } from '../analytics';

const PdfToImage = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [imageFormat, setImageFormat] = useState('jpg');
  
  // Maximum file size in bytes (5MB)
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  
  // Track page view
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackPageView(window.location.pathname);
    }
  }, []);
  
  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Reset states
      setError(null);
      setSuccess(false);
      setDownloadUrl(null);
      
      // Validate that the selected file is a PDF
      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        e.target.value = '';
        return;
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError('File size exceeds 5MB. Please select a smaller file.');
        e.target.value = '';
        return;
      }
      
      setSelectedFile(file);
      trackButtonClick('file_selected', 'pdf_to_image');
    }
  };
  
  // Handle format change
  const handleFormatChange = (e) => {
    setImageFormat(e.target.value);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setError('Please select a PDF file');
      return;
    }
    
    trackButtonClick('conversion_start', 'pdf_to_image');
    setIsLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      const formData = new FormData();
      formData.append('pdf_file', selectedFile);
      // Note: imageFormat is still in state but hidden in UI
      
      // API endpoint for PDF to JPG conversion
      const response = await fetch('https://api.temp2doc.com/convert/pdf-to-jpg', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }
      
      // Handle successful response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      setDownloadUrl(url);
      setSuccess(true);
      trackButtonClick('conversion_success', 'pdf_to_image');
      
      // Optional: Automatically trigger download
      trackButtonClick('download_start', 'pdf_to_image');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedFile.name.replace('.pdf', '')}_images.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      trackButtonClick('conversion_error', 'pdf_to_image');
      setError(`Failed to convert PDF to images: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      <SEOMetaTags 
        title="PDF to Image Converter - Convert PDF to JPG Online | Temp2Doc"
        description="Convert PDF documents to high-quality JPG images online for free. No registration required. Easily transform PDF pages to images for sharing and presentations."
        keywords="pdf to image, pdf to jpg, convert pdf to image, pdf converter, free pdf tools"
        canonicalUrl="https://temp2doc.com/pdf-to-image"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "PDF to Image Converter",
          "applicationCategory": "WebApplication",
          "operatingSystem": "Web browser",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          },
          "description": "Convert PDF documents to high-quality JPG images online for free. No registration required."
        }}
      />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-blue-50 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="max-w-6xl mx-auto text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">PDF to Image Converter</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Convert PDF documents to high-quality images in various formats.
        </p>
        </section>

        {/* Upload Section */}
        <section className="max-w-6xl mx-auto mb-12">
          <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-primary-600" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Your PDF</h2>
          <p className="text-gray-600 text-center max-w-md">
            Each page of the uploaded PDF will be converted and provided as a separate JPG image.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col items-center">
            <label htmlFor="pdf-upload" className="w-full max-w-md">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 cursor-pointer hover:border-primary-500 transition-colors flex flex-col items-center" role="button" aria-label="Select PDF file to convert to images">
                <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-gray-600 mb-2">
                  {selectedFile ? selectedFile.name : 'Select PDF Files'}
                </span>
                <span className="text-xs text-gray-500">
                  Max file size: 5MB
                </span>
              </div>
            </label>
            <input
              id="pdf-upload"
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="hidden"
              aria-label="Upload PDF file"
            />
          </div>

          {/* Image Format selection hidden as requested */}
          <div className="hidden">
            <div className="w-full max-w-md">
              <label className="block text-gray-700 font-medium mb-2">
                Select Image Format
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-primary-600"
                    name="imageFormat"
                    value="jpg"
                    checked={imageFormat === 'jpg'}
                    onChange={handleFormatChange}
                  />
                  <span className="ml-2 text-gray-700">JPG</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-primary-600"
                    name="imageFormat"
                    value="png"
                    checked={imageFormat === 'png'}
                    onChange={handleFormatChange}
                  />
                  <span className="ml-2 text-gray-700">PNG</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio text-primary-600"
                    name="imageFormat"
                    value="tiff"
                    checked={imageFormat === 'tiff'}
                    onChange={handleFormatChange}
                  />
                  <span className="ml-2 text-gray-700">TIFF</span>
                </label>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-4 rounded-lg text-center" role="alert">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-100 text-green-700 p-4 rounded-lg text-center" role="status" aria-live="polite">
              <p className="mb-2">PDF successfully converted to images!</p>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  download={`${selectedFile.name.replace('.pdf', '')}_images.zip`}
                  className="inline-flex items-center text-green-700 font-medium hover:underline"
                  aria-label="Download converted images"
                >
                  <svg className="w-5 h-5 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Download Images
                </a>
              )}
            </div>
          )}

          <div className="flex justify-center">
            <button
              type="submit"
              disabled={!selectedFile || isLoading}
              className={`${
                !selectedFile || isLoading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
              } text-white font-medium py-3 px-8 rounded-lg transition-colors duration-200 flex items-center`}
              aria-label="Convert PDF to images"
              aria-disabled={!selectedFile || isLoading}
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Converting...
                </>
              ) : (
                'Convert to Images'
              )}
            </button>
          </div>
        </form>
          </div>
        </section>

        {/* How It Works */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-primary-600 font-bold text-lg">1</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload</h3>
            <p className="text-gray-600">Select your PDF file from your device.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-primary-600 font-bold text-lg">2</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">JPG Format</h3>
            <p className="text-gray-600"> PDF will be converted and provided as a separate JPG image.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mb-4">
              <span className="text-primary-600 font-bold text-lg">3</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Download</h3>
            <p className="text-gray-600">Download your high-quality images in a ZIP archive.</p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="max-w-6xl mx-auto mb-12">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">Key Features</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">High Quality</h3>
              <p className="text-gray-600 text-sm">
                Convert PDFs to high-quality JPG images maintaining original quality.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">All Pages</h3>
              <p className="text-gray-600 text-sm">
                Each page of your PDF is converted to a separate image file.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="text-primary-600 mb-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Private</h3>
              <p className="text-gray-600 text-sm">
                Your files are processed securely and automatically deleted.
              </p>
            </div>
          </div>
        </section>

        {/* Related Tools Section */}
        <section className="max-w-6xl mx-auto">
          <nav className="bg-white rounded-lg shadow-md p-6 border border-gray-200" aria-label="Related Tools">
        <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Related PDF Tools</h2>
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <li>
            <a href="/chat-with-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Chat PDF With AI</span>
            </a>
          </li>
          <li>
            <a href="/merge-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Merge PDF</span>
            </a>
          </li>
          <li>
            <a href="/split-remove-rotate-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Split Remove Rotate PDF</span>
            </a>
          </li>
          <li>
            <a href="/pdf-to-word" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to Word</span>
            </a>
          </li>
          <li>
            <a href="/pdf-to-excel" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to Excel</span>
            </a>
          </li>
          <li>
            <a href="/pdf-to-powerpoint" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">PDF to PowerPoint</span>
            </a>
          </li>
          <li>
            <a href="/protect-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Protect PDF</span>
            </a>
          </li>
          <li>
            <a href="/unlock-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Unlock PDF</span>
            </a>
          </li>
          <li>
            <a href="/transform-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Repair & Transform PDF</span>
            </a>
          </li>
          <li>
            <a href="/compress-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Compress PDF</span>
            </a>
          </li>
          <li>
            <a href="/diagnose-pdf" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Verify & Diagnose PDF</span>
            </a>
          </li>
          <li>
            <a href="/" className="block bg-white border border-gray-200 rounded-lg p-4 text-center shadow-sm hover:shadow transition-shadow">
              <span className="text-primary-600 font-medium">Back to Home</span>
            </a>
          </li>
        </ul>
          </nav>
        </section>
      </main>
    </>
  );
};

export default PdfToImage;
